import React, { useEffect, useState } from 'react';
import {
  Table, Message, toaster, Loader, Button, Modal, Form, Input, InputNumber,
  Toggle, IconButton, ButtonToolbar, Panel, Tag, TagGroup, Divider, Stack
} from 'rsuite';
import AdminDashboard from '../../components/AdminDashboard';
import {
  getAllTeamVehicles,
  createTeamVehicle,
  updateTeamVehicle,
  deleteTeamVehicle,
  createTeamVehicleLuggage,
  updateTeamVehicleLuggage,
  deleteTeamVehicleLuggage,
  TeamVehicle,
  TeamVehicleLuggage,
  TeamVehicleCreateRequest,
  TeamVehicleLuggageCreateRequest,
  TeamVehicleLuggageUpdateRequest
} from '../../service/VehicleService';
import { authUserData } from '../../service';
import PlusIcon from '@rsuite/icons/Plus';
import EditIcon from '@rsuite/icons/Edit';
import TrashIcon from '@rsuite/icons/Trash';

const VehicleList: React.FC = () => {
  const [vehicles, setVehicles] = useState<TeamVehicle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const user = authUserData();

  // Vehicle Modal State
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [vehicleModalMode, setVehicleModalMode] = useState<'create' | 'edit'>('create');
  const [editingVehicle, setEditingVehicle] = useState<TeamVehicle | null>(null);
  const [vehicleForm, setVehicleForm] = useState<TeamVehicleCreateRequest>({
    teamId: null,
    vehicleID: '',
    vehicleName: '',
    peopleLimit: 4,
    totalLuggageLimit: 3,
    enabled: true,
    displayOrder: 0,
    isActive: true
  });

  // Luggage Modal State
  const [luggageModalOpen, setLuggageModalOpen] = useState(false);
  const [luggageModalMode, setLuggageModalMode] = useState<'create' | 'edit'>('create');
  const [editingLuggage, setEditingLuggage] = useState<TeamVehicleLuggage | null>(null);
  const [selectedVehicleForLuggage, setSelectedVehicleForLuggage] = useState<TeamVehicle | null>(null);
  const [luggageForm, setLuggageForm] = useState<TeamVehicleLuggageCreateRequest>({
    teamVehicleId: 0,
    luggageSize: 26,
    luggageDescription: '',
    quantity: 2
  });

  // Delete Confirmation Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'vehicle' | 'luggage'; item: TeamVehicle | TeamVehicleLuggage } | null>(null);

  // Fetch vehicles from API on component mount
  useEffect(() => {
    const loadVehicles = async () => {
      if (user?.team?.id) {
        setLoading(true);
        try {
          const vehicleList = await getAllTeamVehicles(user.team.id);
          if (vehicleList) {
            setVehicles(vehicleList);
          } else {
            toaster.push(
              <Message type="error" closable>無法載入車輛資料</Message>,
              { placement: 'topCenter' }
            );
          }
        } catch (error) {
          console.error('Error fetching vehicles:', error);
          toaster.push(
            <Message type="error" closable>載入車輛資料時發生錯誤</Message>,
            { placement: 'topCenter' }
          );
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    loadVehicles();
  }, [user?.team?.id]);

  const fetchVehicles = async () => {
    if (user?.team?.id) {
      setLoading(true);
      try {
        const vehicleList = await getAllTeamVehicles(user.team.id);
        if (vehicleList) {
          setVehicles(vehicleList);
        } else {
          toaster.push(
            <Message type="error" closable>無法載入車輛資料</Message>,
            { placement: 'topCenter' }
          );
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
        toaster.push(
          <Message type="error" closable>載入車輛資料時發生錯誤</Message>,
          { placement: 'topCenter' }
        );
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  // ============================================
  // Vehicle CRUD Handlers
  // ============================================

  const handleOpenCreateVehicle = () => {
    setVehicleForm({
      teamId: user?.team?.id || null,
      vehicleID: '',
      vehicleName: '',
      peopleLimit: 4,
      totalLuggageLimit: 3,
      enabled: true,
      displayOrder: vehicles.length,
      isActive: true
    });
    setVehicleModalMode('create');
    setEditingVehicle(null);
    setVehicleModalOpen(true);
  };

  const handleOpenEditVehicle = (vehicle: TeamVehicle) => {
    setVehicleForm({
      teamId: vehicle.teamId,
      vehicleID: vehicle.vehicleID,
      vehicleName: vehicle.vehicleName,
      peopleLimit: vehicle.peopleLimit,
      totalLuggageLimit: vehicle.totalLuggageLimit,
      enabled: vehicle.enabled,
      displayOrder: vehicle.displayOrder,
      isActive: vehicle.isActive
    });
    setVehicleModalMode('edit');
    setEditingVehicle(vehicle);
    setVehicleModalOpen(true);
  };

  const handleSaveVehicle = async () => {
    if (!vehicleForm.vehicleID || !vehicleForm.vehicleName) {
      toaster.push(
        <Message type="warning" closable>請填寫車輛代號和名稱</Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    setSaving(true);
    try {
      if (vehicleModalMode === 'create') {
        const result = await createTeamVehicle(vehicleForm);
        if (result) {
          toaster.push(
            <Message type="success" closable>車輛已新增</Message>,
            { placement: 'topCenter' }
          );
          await fetchVehicles();
        } else {
          throw new Error('Create failed');
        }
      } else if (editingVehicle) {
        const result = await updateTeamVehicle(editingVehicle.id, vehicleForm);
        if (result) {
          toaster.push(
            <Message type="success" closable>車輛已更新</Message>,
            { placement: 'topCenter' }
          );
          await fetchVehicles();
        } else {
          throw new Error('Update failed');
        }
      }
      setVehicleModalOpen(false);
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toaster.push(
        <Message type="error" closable>儲存失敗，請稍後再試</Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteVehicle = (vehicle: TeamVehicle) => {
    setDeleteTarget({ type: 'vehicle', item: vehicle });
    setDeleteModalOpen(true);
  };

  const handleDeleteVehicle = async () => {
    if (!deleteTarget || deleteTarget.type !== 'vehicle') return;

    const vehicle = deleteTarget.item as TeamVehicle;
    setSaving(true);
    try {
      const success = await deleteTeamVehicle(vehicle.id);
      if (success) {
        toaster.push(
          <Message type="success" closable>車輛已刪除</Message>,
          { placement: 'topCenter' }
        );
        await fetchVehicles();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toaster.push(
        <Message type="error" closable>刪除失敗，請稍後再試</Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setSaving(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  // ============================================
  // Luggage CRUD Handlers
  // ============================================

  const handleOpenCreateLuggage = (vehicle: TeamVehicle) => {
    setLuggageForm({
      teamVehicleId: vehicle.id,
      luggageSize: 26,
      luggageDescription: '26吋行李',
      quantity: 2
    });
    setLuggageModalMode('create');
    setEditingLuggage(null);
    setSelectedVehicleForLuggage(vehicle);
    setLuggageModalOpen(true);
  };

  const handleOpenEditLuggage = (luggage: TeamVehicleLuggage, vehicle: TeamVehicle) => {
    setLuggageForm({
      teamVehicleId: luggage.teamVehicleId,
      luggageSize: luggage.luggageSize,
      luggageDescription: luggage.luggageDescription || '',
      quantity: luggage.quantity
    });
    setLuggageModalMode('edit');
    setEditingLuggage(luggage);
    setSelectedVehicleForLuggage(vehicle);
    setLuggageModalOpen(true);
  };

  const handleSaveLuggage = async () => {
    if (luggageForm.luggageSize <= 0 || luggageForm.quantity < 0) {
      toaster.push(
        <Message type="warning" closable>請填寫有效的行李尺寸和數量</Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    setSaving(true);
    try {
      if (luggageModalMode === 'create') {
        const result = await createTeamVehicleLuggage(luggageForm);
        if (result) {
          toaster.push(
            <Message type="success" closable>行李配置已新增</Message>,
            { placement: 'topCenter' }
          );
          await fetchVehicles();
        } else {
          throw new Error('Create failed');
        }
      } else if (editingLuggage) {
        const updateRequest: TeamVehicleLuggageUpdateRequest = {
          luggageSize: luggageForm.luggageSize,
          luggageDescription: luggageForm.luggageDescription,
          quantity: luggageForm.quantity
        };
        const result = await updateTeamVehicleLuggage(editingLuggage.id, updateRequest);
        if (result) {
          toaster.push(
            <Message type="success" closable>行李配置已更新</Message>,
            { placement: 'topCenter' }
          );
          await fetchVehicles();
        } else {
          throw new Error('Update failed');
        }
      }
      setLuggageModalOpen(false);
    } catch (error) {
      console.error('Error saving luggage:', error);
      toaster.push(
        <Message type="error" closable>儲存失敗，請稍後再試</Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteLuggage = (luggage: TeamVehicleLuggage) => {
    setDeleteTarget({ type: 'luggage', item: luggage });
    setDeleteModalOpen(true);
  };

  const handleDeleteLuggage = async () => {
    if (!deleteTarget || deleteTarget.type !== 'luggage') return;

    const luggage = deleteTarget.item as TeamVehicleLuggage;
    setSaving(true);
    try {
      const success = await deleteTeamVehicleLuggage(luggage.id);
      if (success) {
        toaster.push(
          <Message type="success" closable>行李配置已刪除</Message>,
          { placement: 'topCenter' }
        );
        await fetchVehicles();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Error deleting luggage:', error);
      toaster.push(
        <Message type="error" closable>刪除失敗，請稍後再試</Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setSaving(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'vehicle') {
      await handleDeleteVehicle();
    } else {
      await handleDeleteLuggage();
    }
  };

  // ============================================
  // Render Functions
  // ============================================

  const renderLuggageSupporting = (vehicle: TeamVehicle) => {
    const luggages = vehicle.luggageSupporting || [];
    if (luggages.length === 0) {
      return <span style={{ color: '#999' }}>尚無行李配置</span>;
    }
    return (
      <TagGroup>
        {luggages.map((luggage) => (
          <Tag
            key={luggage.id}
            color="blue"
            style={{ cursor: 'pointer', marginBottom: '4px' }}
            onClick={() => handleOpenEditLuggage(luggage, vehicle)}
          >
            {luggage.luggageSize}吋 x{luggage.quantity}
            <IconButton
              icon={<TrashIcon />}
              size="xs"
              appearance="subtle"
              style={{ marginLeft: '4px', padding: '0 2px' }}
              onClick={(e) => {
                e.stopPropagation();
                handleConfirmDeleteLuggage(luggage);
              }}
            />
          </Tag>
        ))}
      </TagGroup>
    );
  };

  return (
    <div>
      <AdminDashboard title="車輛型別管理">
        <Panel bordered style={{ marginBottom: '20px' }}>
          <Stack justifyContent="space-between" alignItems="center" wrap spacing={12}>
            <span style={{ fontSize: '14px', color: '#666' }}>
              車型目錄 · {loading ? '載入中' : `${vehicles.length} 種車型`}
            </span>
            <Button
              appearance="primary"
              startIcon={<PlusIcon />}
              onClick={handleOpenCreateVehicle}
            >
              新增車輛類型
            </Button>
          </Stack>
        </Panel>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
            <Loader size="lg" content="載入中..." />
          </div>
        ) : vehicles.length === 0 ? (
          <Panel bordered style={{ textAlign: 'center', padding: '50px' }}>
            <p style={{ color: '#999', marginBottom: '20px' }}>尚無車輛資料</p>
            <Button appearance="primary" onClick={handleOpenCreateVehicle}>
              新增第一個車輛類型
            </Button>
          </Panel>
        ) : (
          <Table data={vehicles} autoHeight rowHeight={80}>
            <Table.Column width={150} align="center" fixed>
              <Table.HeaderCell>型別名稱</Table.HeaderCell>
              <Table.Cell dataKey="vehicleName" />
            </Table.Column>

            <Table.Column width={120} align="center">
              <Table.HeaderCell>型別代號</Table.HeaderCell>
              <Table.Cell dataKey="vehicleID" />
            </Table.Column>

            <Table.Column width={100} align="center">
              <Table.HeaderCell>乘客上限</Table.HeaderCell>
              <Table.Cell dataKey="peopleLimit" />
            </Table.Column>

            <Table.Column width={280} align="left">
              <Table.HeaderCell>行李配置</Table.HeaderCell>
              <Table.Cell>
                {(rowData: TeamVehicle) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {renderLuggageSupporting(rowData)}
                    <IconButton
                      icon={<PlusIcon />}
                      size="xs"
                      appearance="ghost"
                      color="green"
                      onClick={() => handleOpenCreateLuggage(rowData)}
                      title="新增行李配置"
                    />
                  </div>
                )}
              </Table.Cell>
            </Table.Column>

            <Table.Column width={100} align="center">
              <Table.HeaderCell>行李上限</Table.HeaderCell>
              <Table.Cell dataKey="totalLuggageLimit" />
            </Table.Column>

            <Table.Column width={100} align="center">
              <Table.HeaderCell>排序</Table.HeaderCell>
              <Table.Cell dataKey="displayOrder" />
            </Table.Column>

            <Table.Column width={100} align="center">
              <Table.HeaderCell>狀態</Table.HeaderCell>
              <Table.Cell>
                {(rowData: TeamVehicle) => (
                  <Tag color={rowData.enabled ? 'green' : 'red'}>
                    {rowData.enabled ? '啟用中' : '停用中'}
                  </Tag>
                )}
              </Table.Cell>
            </Table.Column>

            <Table.Column width={150} align="center" fixed="right">
              <Table.HeaderCell>操作</Table.HeaderCell>
              <Table.Cell>
                {(rowData: TeamVehicle) => (
                  <ButtonToolbar>
                    <IconButton
                      icon={<EditIcon />}
                      size="sm"
                      appearance="ghost"
                      color="blue"
                      onClick={() => handleOpenEditVehicle(rowData)}
                      title="編輯"
                    />
                    <IconButton
                      icon={<TrashIcon />}
                      size="sm"
                      appearance="ghost"
                      color="red"
                      onClick={() => handleConfirmDeleteVehicle(rowData)}
                      title="刪除"
                    />
                  </ButtonToolbar>
                )}
              </Table.Cell>
            </Table.Column>
          </Table>
        )}
      </AdminDashboard>

      {/* Vehicle Create/Edit Modal */}
      <Modal open={vehicleModalOpen} onClose={() => setVehicleModalOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>{vehicleModalMode === 'create' ? '新增車輛類型' : '編輯車輛類型'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form fluid>
            <Form.Group>
              <Form.ControlLabel>車輛代號 *</Form.ControlLabel>
              <Input
                value={vehicleForm.vehicleID}
                onChange={(value) => setVehicleForm({ ...vehicleForm, vehicleID: value })}
                placeholder="例如: V04, T01"
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>車輛名稱 *</Form.ControlLabel>
              <Input
                value={vehicleForm.vehicleName}
                onChange={(value) => setVehicleForm({ ...vehicleForm, vehicleName: value })}
                placeholder="例如: 機場四人專車"
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>乘客上限</Form.ControlLabel>
              <InputNumber
                value={vehicleForm.peopleLimit}
                onChange={(value) => setVehicleForm({ ...vehicleForm, peopleLimit: Number(value) || 0 })}
                min={1}
                max={50}
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>行李數量上限</Form.ControlLabel>
              <InputNumber
                value={vehicleForm.totalLuggageLimit}
                onChange={(value) => setVehicleForm({ ...vehicleForm, totalLuggageLimit: Number(value) || 0 })}
                min={0}
                max={50}
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>顯示排序</Form.ControlLabel>
              <InputNumber
                value={vehicleForm.displayOrder}
                onChange={(value) => setVehicleForm({ ...vehicleForm, displayOrder: Number(value) || 0 })}
                min={0}
              />
            </Form.Group>

            <Divider />

            <Form.Group>
              <Stack spacing={20}>
                <Toggle
                  checked={vehicleForm.enabled}
                  onChange={(checked) => setVehicleForm({ ...vehicleForm, enabled: checked })}
                />
                <span>啟用此車輛類型</span>
              </Stack>
            </Form.Group>

            <Form.Group>
              <Stack spacing={20}>
                <Toggle
                  checked={vehicleForm.isActive}
                  onChange={(checked) => setVehicleForm({ ...vehicleForm, isActive: checked })}
                />
                <span>資料有效 (停用後不會顯示)</span>
              </Stack>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setVehicleModalOpen(false)} appearance="subtle">
            取消
          </Button>
          <Button onClick={handleSaveVehicle} appearance="primary" loading={saving}>
            {vehicleModalMode === 'create' ? '新增' : '儲存'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Luggage Create/Edit Modal */}
      <Modal open={luggageModalOpen} onClose={() => setLuggageModalOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>
            {luggageModalMode === 'create' ? '新增行李配置' : '編輯行李配置'}
            {selectedVehicleForLuggage && (
              <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                - {selectedVehicleForLuggage.vehicleName}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form fluid>
            <Form.Group>
              <Form.ControlLabel>行李尺寸 (吋)</Form.ControlLabel>
              <InputNumber
                value={luggageForm.luggageSize}
                onChange={(value) => {
                  const size = Number(value) || 0;
                  setLuggageForm({
                    ...luggageForm,
                    luggageSize: size,
                    luggageDescription: `${size}吋行李`
                  });
                }}
                min={1}
                max={100}
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>描述</Form.ControlLabel>
              <Input
                value={luggageForm.luggageDescription}
                onChange={(value) => setLuggageForm({ ...luggageForm, luggageDescription: value })}
                placeholder="例如: 26吋行李"
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>數量上限</Form.ControlLabel>
              <InputNumber
                value={luggageForm.quantity}
                onChange={(value) => setLuggageForm({ ...luggageForm, quantity: Number(value) || 0 })}
                min={0}
                max={50}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setLuggageModalOpen(false)} appearance="subtle">
            取消
          </Button>
          <Button onClick={handleSaveLuggage} appearance="primary" loading={saving}>
            {luggageModalMode === 'create' ? '新增' : '儲存'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>確認刪除</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget?.type === 'vehicle' ? (
            <p>確定要刪除車輛「{(deleteTarget.item as TeamVehicle).vehicleName}」嗎？此操作會同時刪除所有相關的行李配置。</p>
          ) : (
            <p>確定要刪除此行李配置嗎？</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setDeleteModalOpen(false)} appearance="subtle">
            取消
          </Button>
          <Button onClick={handleDelete} appearance="primary" color="red" loading={saving}>
            刪除
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default VehicleList;